import { useEffect, useMemo, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import api, { formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import QrScanner from "../components/QrScanner";
import { toast } from "sonner";
import { Gift, Search, Loader2, Download, UserCheck, Minus, Plus, X, Sparkles, QrCode, Ticket } from "lucide-react";

const eur = (n) => `€${Number(n || 0).toFixed(2).replace(".", ",")}`;
const dIt = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString("it-IT", { timeZone: "Europe/Rome" }); } catch { return "—"; } };

// Categorie CRM (segmentazione marketing). I prodotti suggeriti sono riferimenti REALI del catalogo menu.
const CRM_CATS = [
  { key: "CAFFETTERIA", label: "Caffetteria", menu: ["Caffetteria"] },
  { key: "APERITIVO", label: "Aperitivo", menu: ["Aperitivi classici"] },
  { key: "SPRITZ", label: "Spritz", menu: ["Gli Spritz"] },
  { key: "COCKTAIL", label: "Cocktail", menu: ["Cocktail e Gin Tonic", "DrinkList Special"] },
  { key: "GIN TONIC", label: "Gin Tonic", menu: ["Cocktail e Gin Tonic"], nameRx: /gin|tonic/i },
  { key: "BIRRA", label: "Birra", menu: ["Birre Artigianali"] },
  { key: "ROSTICCERIA", label: "Rosticceria", menu: ["Rosticceria"] },
  { key: "BURGER", label: "Burger", menu: ["Burger"] },
  { key: "PINSA", label: "Pinsa", menu: ["Pinsa Romana"] },
  { key: "PRANZO", label: "Pranzo", menu: [] },
  { key: "DOLCI", label: "Dolci", menu: ["Dolci"] },
  { key: "EVENTO", label: "Evento", menu: [] },
  { key: "ALTRO", label: "Altro", menu: [] },
];

export default function CrmLoyalty() {
  const [code, setCode] = useState("");
  const [cust, setCust] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [stats, setStats] = useState(null);
  const [idemKey, setIdemKey] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const [scanVoucherOpen, setScanVoucherOpen] = useState(false);
  const [voucher, setVoucher] = useState(null);
  const [voucherConfirm, setVoucherConfirm] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [catalog, setCatalog] = useState({ cats: [], dishes: [] });
  // Consumi
  const [selCats, setSelCats] = useState({});   // { KEY: true }
  const [selProds, setSelProds] = useState({}); // { product_id: {category, name, qty} }
  const [expanded, setExpanded] = useState(null);
  const [prodSearch, setProdSearch] = useState("");

  const loadStats = () => api.get("/loyalty/stats").then(({ data }) => setStats(data)).catch(() => {});
  useEffect(() => { loadStats(); }, []);
  useEffect(() => {
    api.get("/pos/catalog").then(({ data }) => setCatalog({ cats: data.categories || [], dishes: data.dishes || [] })).catch(() => {});
  }, []);

  const catName = useMemo(() => Object.fromEntries(catalog.cats.map((c) => [c.id, c.name])), [catalog.cats]);
  const dishes = useMemo(() => catalog.dishes.map((d) => ({ id: d.id, name: d.name, cat: catName[d.category_id] })), [catalog.dishes, catName]);

  const suggestFor = (crm) => {
    if (!crm.menu.length) return [];
    let list = dishes.filter((d) => crm.menu.includes(d.cat));
    if (crm.nameRx) list = list.filter((d) => crm.nameRx.test(d.name));
    return list.slice(0, 8);
  };
  const searchResults = useMemo(() => {
    const q = prodSearch.trim().toLowerCase();
    if (!q) return [];
    return dishes.filter((d) => d.name.toLowerCase().includes(q)).slice(0, 12);
  }, [prodSearch, dishes]);

  const resetConsumption = () => { setSelCats({}); setSelProds({}); setExpanded(null); setProdSearch(""); };

  const search = async () => {
    if (!code.trim()) return;
    setBusy(true); setCust(null);
    try {
      const { data } = await api.get("/loyalty/search", { params: { code: code.trim() } });
      if (!data.length) { toast.error("Nessun cliente con questo codice"); return; }
      const { data: full } = await api.get(`/loyalty/customer/${data[0].id}`);
      setCust(full); setAmount(""); resetConsumption();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onScan = async (text) => {
    setScanOpen(false);
    let token = (text || "").trim();
    const idx = token.indexOf("/club/me/");
    if (idx >= 0) token = token.slice(idx + "/club/me/".length);
    token = token.split("?")[0].split("#")[0].replace(/\/+$/, "");
    if (!token) { toast.error("QR non valido"); return; }
    setBusy(true); setCust(null);
    try {
      const { data } = await api.get(`/loyalty/resolve-qr/${encodeURIComponent(token)}`);
      const { data: full } = await api.get(`/loyalty/customer/${data.customer_id}`);
      setCust(full); setAmount(""); resetConsumption();
      toast.success(`${data.first_name} ${data.last_name} — ${data.customer_code}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onScanVoucher = async (text) => {
    setScanVoucherOpen(false);
    let t = (text || "").trim();
    const idx = t.indexOf("/club/voucher/");
    if (idx >= 0) t = t.slice(idx + "/club/voucher/".length);
    t = t.split("?")[0].split("#")[0].replace(/\/+$/, "");
    if (!t) { toast.error("QR buono non valido"); return; }
    setBusy(true);
    try {
      const { data } = await api.get(`/loyalty/voucher/resolve/${encodeURIComponent(t)}`);
      setVoucher(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doRedeem = async () => {
    if (!voucher) return;
    setRedeeming(true);
    try {
      const { data } = await api.post(`/loyalty/voucher/${voucher.voucher_id}/redeem`);
      toast.success(`Buono ${data.voucher_code} utilizzato · ${eur(data.amount)}`);
      setVoucher(null); setVoucherConfirm(false);
      if (cust) { const { data: full } = await api.get(`/loyalty/customer/${cust.id}`); setCust(full); }
      loadStats();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det?.code === "ALREADY_REDEEMED") toast.error(`Buono già utilizzato il ${dIt(det.redeemed_at)}`);
      else if (det?.code === "EXPIRED") toast.error("Buono scaduto, non più utilizzabile");
      else if (det?.code === "CANCELLED") toast.error("Buono annullato");
      else toast.error(formatApiError(det));
      setVoucherConfirm(false); setVoucher(null);
    }
    finally { setRedeeming(false); }
  };

  const amt = parseFloat((amount || "").replace(",", ".")) || 0;
  const preview = Math.round(amt * 10) / 100;
  const expiry = new Date(Date.now() + 30 * 86400000);

  const toggleCat = (key) => {
    setSelCats((s) => {
      const n = { ...s };
      if (n[key]) {
        delete n[key];
        setSelProds((p) => Object.fromEntries(Object.entries(p).filter(([, v]) => v.category !== key)));
        setExpanded((e) => (e === key ? null : e));
      } else {
        n[key] = true; setExpanded(key); setProdSearch("");
      }
      return n;
    });
  };
  const toggleProd = (crmKey, dish) => {
    setSelProds((p) => {
      const n = { ...p };
      if (n[dish.id]) delete n[dish.id];
      else n[dish.id] = { category: crmKey, name: dish.name, qty: 1 };
      return n;
    });
    setSelCats((s) => ({ ...s, [crmKey]: true }));
  };
  const setQty = (pid, delta) => setSelProds((p) => {
    const it = p[pid]; if (!it) return p;
    return { ...p, [pid]: { ...it, qty: Math.max(1, Math.min(99, (it.qty || 1) + delta)) } };
  });

  const consumptionSummary = useMemo(() => {
    const prodByCat = {};
    Object.values(selProds).forEach((v) => { (prodByCat[v.category] ||= []).push(v); });
    return Object.keys(selCats).map((cat) => ({
      cat, label: CRM_CATS.find((c) => c.key === cat)?.label || cat, products: prodByCat[cat] || [],
    }));
  }, [selCats, selProds]);

  const buildItems = () => {
    const items = [];
    const catsWithProds = new Set();
    Object.entries(selProds).forEach(([pid, v]) => {
      catsWithProds.add(v.category);
      items.push({ category: v.category, product_id: pid, product_name_snapshot: v.name, quantity: v.qty });
    });
    Object.keys(selCats).forEach((cat) => {
      if (!catsWithProds.has(cat)) items.push({ category: cat, product_id: null, product_name_snapshot: null, quantity: null });
    });
    return items;
  };

  const openConfirm = () => {
    if (amt <= 0) { toast.error("Inserisci un importo valido"); return; }
    setIdemKey(window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now()));
    setConfirm(true);
  };

  const doConfirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/loyalty/visits", { customer_id: cust.id, amount_paid: amt, idempotency_key: idemKey, consumption_items: buildItems() });
      setConfirm(false);
      toast.success(`Spesa registrata · credito ${eur(data.reward_amount)} (scade ${dIt(data.expires_at)})`);
      const { data: full } = await api.get(`/loyalty/customer/${cust.id}`);
      setCust(full); setAmount(""); resetConsumption(); loadStats();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const downloadQr = () => {
    const c = document.querySelector('[data-testid="club-table-qr"] canvas');
    if (!c) return;
    const a = document.createElement("a"); a.href = c.toDataURL("image/png"); a.download = "qr-club-dogana.png"; a.click();
  };

  const prefs = cust?.preferences;

  return (
    <div className="space-y-6 pb-10" data-testid="loyalty-page">
      <div className="flex items-center gap-2 text-primary"><Gift className="w-6 h-6" /><h1 className="font-dash text-2xl font-bold text-foreground">Club Dogana — Registra spesa</h1></div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="loyalty-stats">
          {[["Iscritti", stats.total_members], ["Iscritti oggi", stats.members_today], ["Visite oggi", stats.visits_today],
            ["Spesa oggi", eur(stats.spent_today)], ["Credito generato oggi", eur(stats.credit_today)], ["Credito attivo totale", eur(stats.active_credit_total)]].map(([l, v]) => (
            <Card key={l} className="p-3 rounded-xl"><p className="text-xs text-muted-foreground">{l}</p><p className="text-lg font-bold">{v}</p></Card>
          ))}
        </div>
      )}

      <Card className="p-5 rounded-xl max-w-xl">
        <Label className="text-sm font-medium">Inserisci codice cliente</Label>
        <div className="flex gap-2 mt-2">
          <Input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="DG-_____" className="h-12 text-lg font-semibold tracking-wider" data-testid="loyalty-code-input" />
          <Button onClick={search} disabled={busy} className="h-12 rounded-full px-6" data-testid="loyalty-search-btn">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}</Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">Ricerca principale per codice. Puoi anche usare telefono/nome dall'anagrafica CRM.</p>
        <Button onClick={() => setScanOpen(true)} variant="outline" className="w-full h-12 rounded-full mt-3 font-semibold" data-testid="loyalty-scan-btn"><QrCode className="w-5 h-5 mr-2" /> SCANSIONA QR CLIENTE</Button>
        <Button onClick={() => setScanVoucherOpen(true)} className="w-full h-12 rounded-full mt-2 font-semibold" data-testid="loyalty-redeem-scan-btn"><Ticket className="w-5 h-5 mr-2" /> RISCATTA BUONO</Button>
      </Card>

      {cust && (
        <Card className="p-5 rounded-xl max-w-xl" data-testid="loyalty-customer-card">
          <div className="flex items-center gap-2"><UserCheck className="w-5 h-5 text-primary" /><h2 className="font-semibold text-lg">{cust.first_name} {cust.last_name}</h2></div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-3 text-sm">
            <span className="text-muted-foreground">Codice</span><span className="font-semibold">{cust.customer_code}</span>
            <span className="text-muted-foreground">Telefono</span><span>{cust.phone_masked}</span>
            <span className="text-muted-foreground">Credito disponibile</span><span className="font-semibold text-emerald-600">{eur(cust.credit?.disponibile)}</span>
            <span className="text-muted-foreground">Ultima visita</span><span>{dIt(cust.last_visit)}</span>
            <span className="text-muted-foreground">Totale visite</span><span>{cust.total_visits}</span>
          </div>

          <div className="mt-5 pt-4 border-t">
            <Label className="text-sm font-medium">Importo pagato</Label>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-2xl font-bold text-muted-foreground">€</span>
              <Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" placeholder="0,00" className="h-14 text-2xl font-bold" data-testid="loyalty-amount-input" />
            </div>
            {amt > 0 && (
              <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-center justify-between">
                <span className="text-sm text-emerald-800">Credito da generare (10%)</span>
                <span className="text-xl font-bold text-emerald-700" data-testid="loyalty-preview-credit">{eur(preview)}</span>
              </div>
            )}
          </div>

          {/* ---------------------------- COSA HA CONSUMATO (facoltativo) */}
          <div className="mt-5 pt-4 border-t" data-testid="consumption-section">
            <Label className="text-sm font-medium">Cosa ha consumato? <span className="text-xs text-muted-foreground font-normal">(facoltativo)</span></Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {CRM_CATS.map((c) => {
                const on = !!selCats[c.key];
                return (
                  <button key={c.key} type="button" onClick={() => toggleCat(c.key)} data-testid={`cons-cat-${c.key.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background text-foreground border-border hover:border-primary/60"}`}>
                    {c.label}{on ? " ✓" : ""}
                  </button>
                );
              })}
            </div>

            {expanded && (() => {
              const crm = CRM_CATS.find((c) => c.key === expanded);
              const suggestions = suggestFor(crm);
              return (
                <div className="mt-3 rounded-xl border bg-muted/30 p-3" data-testid="consumption-products">
                  <div className="flex items-center justify-between">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">{crm.label} — prodotto preciso (facoltativo)</p>
                    <button type="button" onClick={() => setExpanded(null)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
                  </div>
                  {suggestions.length > 0 && (
                    <>
                      <p className="text-[11px] text-muted-foreground mt-2 mb-1">Più usati</p>
                      <div className="flex flex-wrap gap-2">
                        {suggestions.map((d) => (
                          <button key={d.id} type="button" onClick={() => toggleProd(expanded, d)} data-testid={`cons-prod-${d.id}`}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${selProds[d.id] ? "bg-emerald-600 text-white border-emerald-600" : "bg-background border-border hover:border-primary/60"}`}>
                            {d.name}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                  <div className="mt-3">
                    <Input value={prodSearch} onChange={(e) => setProdSearch(e.target.value)} placeholder="Cerca nel menu…" className="h-9 text-sm" data-testid="cons-search-input" />
                    {searchResults.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {searchResults.map((d) => (
                          <button key={d.id} type="button" onClick={() => toggleProd(expanded, d)} data-testid={`cons-search-prod-${d.id}`}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${selProds[d.id] ? "bg-emerald-600 text-white border-emerald-600" : "bg-background border-border hover:border-primary/60"}`}>
                            {d.name}<span className="text-[10px] opacity-60 ml-1">{d.cat}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {Object.keys(selProds).length > 0 && (
              <div className="mt-3 space-y-2" data-testid="consumption-selected">
                {Object.entries(selProds).map(([pid, v]) => (
                  <div key={pid} className="flex items-center justify-between gap-2 text-sm bg-background border rounded-xl px-3 py-2">
                    <span className="truncate"><span className="text-muted-foreground text-xs mr-1">{CRM_CATS.find((c) => c.key === v.category)?.label}</span>{v.name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button type="button" size="icon" variant="outline" className="h-7 w-7 rounded-full" onClick={() => setQty(pid, -1)} data-testid={`cons-qty-dec-${pid}`}><Minus className="w-3.5 h-3.5" /></Button>
                      <span className="w-5 text-center font-semibold" data-testid={`cons-qty-${pid}`}>{v.qty}</span>
                      <Button type="button" size="icon" variant="outline" className="h-7 w-7 rounded-full" onClick={() => setQty(pid, 1)} data-testid={`cons-qty-inc-${pid}`}><Plus className="w-3.5 h-3.5" /></Button>
                      <button type="button" onClick={() => toggleProd(v.category, { id: pid })} className="text-destructive ml-1"><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button onClick={openConfirm} disabled={amt <= 0 || busy} className="w-full h-13 rounded-full text-base font-semibold mt-4" data-testid="loyalty-confirm-btn">CONFERMA SPESA</Button>

          {/* ---------------------------- PREFERENZE / CONSUMI */}
          {prefs && prefs.total_visits > 0 && (
            <div className="mt-5 pt-4 border-t" data-testid="consumption-prefs">
              <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-primary" /><p className="text-xs uppercase tracking-widest text-muted-foreground">Preferenze / Consumi</p></div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <span className="text-muted-foreground">Categoria preferita</span><span className="font-semibold">{prefs.top_category || "—"}</span>
                <span className="text-muted-foreground">Spesa media</span><span>{eur(prefs.avg_spent)}</span>
                <span className="text-muted-foreground">Totale speso</span><span>{eur(prefs.total_spent)}</span>
              </div>
              {Object.keys(prefs.category_visits || {}).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {Object.entries(prefs.category_visits).map(([c, n]) => (
                    <span key={c} className="px-3 py-1 rounded-full text-xs bg-primary/10 text-primary font-medium" data-testid={`pref-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}>{(CRM_CATS.find((x) => x.key === c)?.label || c)} · {n}</span>
                  ))}
                </div>
              )}
              {prefs.top_products?.length > 0 && (
                <div className="mt-3">
                  <p className="text-[11px] text-muted-foreground mb-1">Prodotti più acquistati</p>
                  <div className="space-y-1">
                    {prefs.top_products.map((p) => (
                      <div key={p.name} className="flex items-center justify-between text-sm"><span>{p.name}</span><span className="font-semibold">×{p.qty}</span></div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {cust.vouchers?.length > 0 && (
            <div className="mt-4 pt-3 border-t" data-testid="customer-vouchers">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Buoni</p>
                {cust.voucher_stats && <span className="text-xs text-muted-foreground">Attivi {eur(cust.voucher_stats.active_value)} · Riscatto {Math.round((cust.voucher_stats.redemption_rate || 0) * 100)}%</span>}
              </div>
              <div className="space-y-1">
                {cust.vouchers.map((v) => {
                  const badge = v.status === "ACTIVE" ? "bg-amber-100 text-amber-800" : v.status === "REDEEMED" ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground";
                  const lbl = v.status === "ACTIVE" ? "ATTIVO" : v.status === "REDEEMED" ? "UTILIZZATO" : v.status === "EXPIRED" ? "SCADUTO" : "ANNULLATO";
                  return (
                    <div key={v.voucher_code} className="flex items-center justify-between gap-2 text-sm bg-background border rounded-xl px-3 py-2" data-testid={`voucher-row-${v.voucher_code}`}>
                      <div className="min-w-0">
                        <div className="font-semibold">{eur(v.amount)} <span className="text-xs text-muted-foreground font-normal">{v.voucher_code}</span></div>
                        <div className="text-xs text-muted-foreground">creato {dIt(v.created_at)} · scade {dIt(v.expires_at)}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge}`}>{lbl}</span>
                        {v.voucher_token && <a href={`/club/voucher/${v.voucher_token}`} target="_blank" rel="noreferrer" className="text-xs underline text-primary" data-testid={`voucher-view-${v.voucher_code}`}>Visualizza</a>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {cust.visits?.length > 0 && (
            <div className="mt-4 pt-3 border-t">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Storico visite</p>
              <div className="space-y-1">
                {cust.visits.slice(0, 8).map((v) => (
                  <div key={v.id} className="flex items-center justify-between text-sm">
                    <span className={v.status === "CANCELLED" ? "line-through text-muted-foreground" : ""}>{dIt(v.created_at)} · {eur(v.amount_paid)}</span>
                    <span className={v.status === "CANCELLED" ? "text-destructive" : "text-emerald-600"}>{v.status === "CANCELLED" ? "annullata" : `+${eur(v.reward_amount)}`}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      <Card className="p-5 rounded-xl max-w-xl" data-testid="club-table-qr">
        <h2 className="font-semibold">QR da tavolo → Club Dogana</h2>
        <p className="text-sm text-muted-foreground mt-1">Punta a {stats?.club_url || "https://dogana2.it/club"}. Scaricalo e stampalo per i tavoli.</p>
        <div className="mt-3 inline-block bg-white p-3 rounded-xl border">
          <QRCodeCanvas value={stats?.club_url || "https://dogana2.it/club"} size={640} level="M" style={{ width: 160, height: 160 }} />
        </div>
        <div><Button onClick={downloadQr} variant="outline" className="rounded-full mt-3"><Download className="w-4 h-4 mr-1" /> Scarica QR</Button></div>
      </Card>

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent data-testid="loyalty-confirm-dialog">
          <DialogHeader><DialogTitle>Conferma registrazione spesa</DialogTitle></DialogHeader>
          {cust && (
            <div className="text-sm space-y-2">
              <p><strong>{cust.first_name} {cust.last_name} — {cust.customer_code}</strong></p>
              <div className="flex items-center justify-between"><span className="text-muted-foreground">Spesa</span><strong>{eur(amt)}</strong></div>
              {consumptionSummary.length > 0 && (
                <div>
                  <p className="text-muted-foreground">Consumo</p>
                  <ul className="mt-1 space-y-0.5" data-testid="confirm-consumption">
                    {consumptionSummary.map((c) => (
                      c.products.length > 0
                        ? c.products.map((p, i) => <li key={c.cat + i}>• {p.name} ×{p.qty}</li>)
                        : <li key={c.cat}>• {c.label}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex items-center justify-between"><span className="text-muted-foreground">Credito</span><strong className="text-emerald-600">{eur(preview)}</strong></div>
              <div className="flex items-center justify-between"><span className="text-muted-foreground">Scadenza</span><strong>{dIt(expiry.toISOString())}</strong></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)} className="rounded-full" data-testid="loyalty-cancel-dialog">Annulla</Button>
            <Button onClick={doConfirm} disabled={busy} className="rounded-full" data-testid="loyalty-confirm-dialog-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Conferma ${eur(amt)}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QrScanner open={scanOpen} onClose={() => setScanOpen(false)} onResult={onScan} />
      <QrScanner open={scanVoucherOpen} onClose={() => setScanVoucherOpen(false)} onResult={onScanVoucher} />

      <Dialog open={!!voucher} onOpenChange={(o) => !o && setVoucher(null)}>
        <DialogContent data-testid="voucher-resolve-dialog">
          <DialogHeader><DialogTitle>Buono Club Dogana</DialogTitle></DialogHeader>
          {voucher && (
            <div className="text-sm space-y-2">
              <p><strong>{voucher.first_name} {voucher.last_name} — {voucher.customer_code}</strong></p>
              <div className="flex items-center justify-between"><span className="text-muted-foreground">Buono</span><strong>{voucher.voucher_code}</strong></div>
              <div className="flex items-center justify-between"><span className="text-muted-foreground">Valore</span><strong className="text-lg text-emerald-600" data-testid="voucher-value">{eur(voucher.amount)}</strong></div>
              <div className="flex items-center justify-between"><span className="text-muted-foreground">Scadenza</span><strong>{dIt(voucher.expires_at)}</strong></div>
              <div className="flex items-center justify-between"><span className="text-muted-foreground">Stato</span>
                <strong className={voucher.status === "ACTIVE" ? "text-amber-700" : "text-muted-foreground"} data-testid="voucher-status">
                  {voucher.status === "ACTIVE" ? "ATTIVO" : voucher.status === "REDEEMED" ? `UTILIZZATO il ${dIt(voucher.redeemed_at)}` : voucher.status === "EXPIRED" ? "SCADUTO" : "ANNULLATO"}
                </strong>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoucher(null)} className="rounded-full" data-testid="voucher-close-btn">Chiudi</Button>
            {voucher?.status === "ACTIVE" && (
              <Button onClick={() => setVoucherConfirm(true)} className="rounded-full" data-testid="voucher-use-btn">UTILIZZA BUONO {eur(voucher.amount)}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={voucherConfirm} onOpenChange={setVoucherConfirm}>
        <DialogContent data-testid="voucher-confirm-dialog">
          <DialogHeader><DialogTitle>Confermi l'utilizzo?</DialogTitle></DialogHeader>
          {voucher && <p className="text-sm">Confermi di utilizzare il buono da <strong>{eur(voucher.amount)}</strong> di <strong>{voucher.first_name} {voucher.last_name}</strong>? Questa operazione renderà il buono non più utilizzabile.</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoucherConfirm(false)} className="rounded-full" data-testid="voucher-cancel-redeem">Annulla</Button>
            <Button onClick={doRedeem} disabled={redeeming} className="rounded-full" data-testid="voucher-confirm-redeem">{redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : "CONFERMA UTILIZZO"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
